/*
 * ТЗ V5 №16: модель многодорожечного Ганта кадров (frame-gantt-v3).
 * Здесь нет React: параметры запроса, чтение ответа, цвет точки по метрике,
 * раскладка пересекающихся событий по поддорожкам и подписи.
 *
 * Главное правило цвета: 0 людей — прозрачная точка («Людей нет»), а null,
 * отрицательное или нечисловое значение — серая («Нет достоверных данных»).
 * Неизвестное никогда не приравнивается к нулю.
 */
import { formatInTimezone } from '../../utils/formatInTimezone';

export const FRAME_GANTT_PAGE_SIZE = 500;
export const FRAME_GANTT_MAX_LIMIT = 1000;
/** Окно без фильтра по дате: последние сутки. Сервер принимает не больше 31 дня. */
export const FRAME_GANTT_DEFAULT_WINDOW_MS = 24 * 60 * 60 * 1000;

export const PEOPLE_GANTT_METRICS = [
  { key: 'people_count', label: 'Люди', kind: 'people' },
  { key: 'hats', label: 'Головные уборы', kind: 'ppe' },
  { key: 'masks', label: 'Маски', kind: 'ppe' },
  { key: 'gloves', label: 'Перчатки', kind: 'ppe' },
  { key: 'frame_brt', label: 'Яркость кадра', kind: 'measured', defaultHidden: true },
  { key: 'people_type', label: 'Тип людей', kind: 'measured', defaultHidden: true },
];

export const EQUIPMENT_GANTT_METRICS = [
  { key: 'signal_type', label: 'Тип сигнала', kind: 'measured' },
  { key: 'light_brt', label: 'Уровень яркости', kind: 'measured' },
  { key: 'frame_brt', label: 'Яркость кадра', kind: 'measured', defaultHidden: true },
];

export const EVENT_CATEGORIES = [
  { id: 'violation', label: 'Нарушения', itemLabel: 'Нарушение' },
  { id: 'business', label: 'Бизнес-метрики', itemLabel: 'Бизнес-метрика' },
];

/** Сортировка таблицы не относится к фильтру кадров. */
const NON_FILTER_KEYS = new Set(['sortBy', 'sortOrder', 'limit', 'offset']);

const isEmptyValue = (value) => value === '' || value === null || value === undefined
  || (Array.isArray(value) && value.length === 0);

/**
 * Окно запроса. Даты фильтра уходят как есть — так же, как в таблицу и в
 * выгрузку; без даты окно — последние сутки от `now`.
 */
export const resolveGanttWindow = (filters = {}, now = Date.now()) => {
  const end = filters.end_date || new Date(now).toISOString();
  let start = filters.start_date;
  if (!start) {
    const endMs = Date.parse(end);
    start = new Date((Number.isFinite(endMs) ? endMs : now) - FRAME_GANTT_DEFAULT_WINDOW_MS).toISOString();
  }
  return { start_date: start, end_date: end };
};

export const buildFrameGanttParams = ({
  filters = {}, window, metrics = [], eventTypes = [], offset = 0, limit = FRAME_GANTT_PAGE_SIZE,
}) => {
  const params = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (NON_FILTER_KEYS.has(key) || isEmptyValue(value)) return;
    params[key] = value;
  });
  Object.assign(params, window);
  if (metrics.length > 0) params.metrics = metrics;
  // Пустой выбор категорий — события не нужны совсем.
  if (eventTypes.length > 0) params.event_types = eventTypes;
  else params.include_events = false;
  params.limit = Math.min(Math.max(1, limit), FRAME_GANTT_MAX_LIMIT);
  params.offset = Math.max(0, offset);
  params.is_total_needed = offset === 0;
  return params;
};

const toMs = (value) => {
  const ms = typeof value === 'string' ? Date.parse(value) : NaN;
  return Number.isFinite(ms) ? ms : null;
};

export const normalizeFrameGanttResponse = (data) => {
  const points = (Array.isArray(data?.points) ? data.points : [])
    .map((point) => ({ ...point, atMs: toMs(point?.at) }))
    .filter((point) => point.frame_id != null && point.atMs != null);
  const events = (Array.isArray(data?.events) ? data.events : [])
    .map((event) => ({ ...event, fromMs: toMs(event?.from_at), toMs: toMs(event?.to_at) }))
    .filter((event) => event.fromMs != null);
  const offset = Number.isInteger(data?.offset) ? data.offset : 0;
  return {
    timezone: data?.display_timezone || '',
    fromMs: toMs(data?.from_at),
    toMs: toMs(data?.to_at),
    points,
    events,
    total: Number.isInteger(data?.total) ? data.total : null,
    offset,
    hasMore: Boolean(data?.has_more),
    nextOffset: Number.isInteger(data?.next_offset) ? data.next_offset : offset + points.length,
    metricsMeta: data?.metrics_meta && typeof data.metrics_meta === 'object' ? data.metrics_meta : {},
  };
};

/**
 * Склейка подгруженных страниц в одну шкалу. Страницы идут по времени
 * (created_at, id); повтор кадра или события на стыке страниц не дублируется.
 * Мета (часовой пояс, total, metrics_meta) — из первой страницы, признак
 * продолжения — из последней.
 */
export const mergeFrameGanttPages = (pages) => {
  const list = (pages || []).filter(Boolean);
  if (list.length === 0) return null;
  const first = list[0];
  const last = list[list.length - 1];
  const seenFrames = new Set();
  const points = [];
  const seenEvents = new Set();
  const events = [];
  list.forEach((page) => {
    page.points.forEach((point) => {
      const id = String(point.frame_id);
      if (seenFrames.has(id)) return;
      seenFrames.add(id);
      points.push(point);
    });
    page.events.forEach((event) => {
      const id = `${event.category}|${event.event_id}|${event.fromMs}`;
      if (seenEvents.has(id)) return;
      seenEvents.add(id);
      events.push(event);
    });
  });
  return {
    ...first,
    points,
    events,
    hasMore: last.hasMore,
    nextOffset: last.nextOffset,
  };
};

/** Метрика Ганта → флаг детектора в detection_configs камеры. */
const METRIC_DETECTORS = {
  people_count: 'people_detector',
  hats: 'hats_detector',
  masks: 'masks_detector',
  gloves: 'gloves_detector',
};

/**
 * Метрики, выключенные в настройках камеры. Отключённый детектор не пишет
 * значения — без этого дорожка выглядела бы как «нет кадров». Флаг читаем
 * только явный false: отсутствие ключа ничего не значит.
 */
export const disabledMetricsFromConfig = (detectionConfigs) => {
  let config = detectionConfigs;
  if (typeof config === 'string') {
    try {
      config = JSON.parse(config);
    } catch {
      return [];
    }
  }
  if (!config || typeof config !== 'object') return [];
  return Object.entries(METRIC_DETECTORS)
    .filter(([, detector]) => config[detector] === false)
    .map(([metric]) => metric);
};

/**
 * Включена ли метрика. metrics_meta из ответа Ганта главнее настроек камеры:
 * backend знает фактическое состояние детектора на период.
 */
export const isMetricDisabled = (metricKey, metricsMeta, disabledKeys) => {
  const enabled = metricsMeta?.[metricKey]?.enabled;
  if (typeof enabled === 'boolean') return !enabled;
  return Array.isArray(disabledKeys) && disabledKeys.includes(metricKey);
};

const validCount = (value) => (typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null);

const KNOWN_STATES = new Set(['present', 'absent', 'compliant', 'mismatch', 'unknown', 'measured', 'observed']);

/**
 * Состояние точки метрики. Состояние сервера главнее; без него — та же логика
 * на клиенте. PPE сравнивается с числом людей в том же кадре.
 */
export const deriveMetricState = (metric, point) => {
  const entry = point?.metrics?.[metric.key];
  const value = entry && typeof entry === 'object' ? entry.value : entry;
  if (entry && typeof entry === 'object' && KNOWN_STATES.has(entry.state)) {
    return { state: entry.state, value: value ?? null };
  }
  if (metric.kind === 'people') {
    const count = validCount(value);
    if (count == null) return { state: 'unknown', value: null };
    return { state: count > 0 ? 'present' : 'absent', value: count };
  }
  if (metric.kind === 'ppe') {
    const count = validCount(value);
    const peopleEntry = point?.metrics?.people_count;
    const people = validCount(peopleEntry && typeof peopleEntry === 'object' ? peopleEntry.value : peopleEntry);
    if (count == null || people == null) return { state: 'unknown', value: count };
    if (people === 0) return { state: count === 0 ? 'absent' : 'unknown', value: count };
    return { state: count === people ? 'compliant' : 'mismatch', value: count };
  }
  const present = value !== null && value !== undefined && value !== ''
    && !(Array.isArray(value) && value.length === 0);
  return { state: present ? 'measured' : 'unknown', value: present ? value : null };
};

/** Семантический тон для цвета: ok зелёный, bad красный, empty прозрачный, info нейтральный, unknown серый. */
export const STATE_TONES = {
  present: 'ok',
  compliant: 'ok',
  mismatch: 'bad',
  absent: 'empty',
  measured: 'info',
  observed: 'info',
  unknown: 'unknown',
};

const peopleOf = (point) => {
  const entry = point?.metrics?.people_count;
  return validCount(entry && typeof entry === 'object' ? entry.value : entry);
};

const formatValue = (value) => (Array.isArray(value) ? value.join(', ') : String(value));

export const describeMetricState = (metric, point, { state, value }) => {
  if (state === 'unknown') return 'Нет достоверных данных';
  if (metric.kind === 'people') return state === 'absent' ? 'Людей нет' : `Людей ${value}`;
  if (metric.kind === 'ppe') {
    if (state === 'absent') return `${metric.label} 0 · людей нет`;
    const people = peopleOf(point);
    return people != null ? `${metric.label} ${value} из ${people}` : `${metric.label} ${value}`;
  }
  return `${metric.label} ${formatValue(value)}`;
};

/**
 * Пересекающиеся интервалы — по поддорожкам, чтобы не стирать перекрытия.
 * Интервал полуоткрытый [from, to): событие, начавшееся ровно в конце другого,
 * встаёт на ту же поддорожку.
 */
export const layoutEventLanes = (events, nowMs = Date.now()) => {
  const sorted = [...events].sort((a, b) => a.fromMs - b.fromMs || String(a.event_id).localeCompare(String(b.event_id)));
  const laneEnds = [];
  const placed = sorted.map((event) => {
    const endMs = event.toMs ?? nowMs;
    let lane = laneEnds.findIndex((laneEnd) => laneEnd <= event.fromMs);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(endMs);
    } else {
      laneEnds[lane] = endMs;
    }
    return { ...event, endMs, lane };
  });
  return { events: placed, laneCount: laneEnds.length };
};

export const describeEvent = (event, category, timezone) => {
  const head = [category?.itemLabel, event.rule_name || event.rule_title, event.event_id != null ? `№${event.event_id}` : '']
    .filter(Boolean)
    .join(' · ');
  const from = formatInTimezone(event.from_at, timezone);
  const to = event.to_at ? formatInTimezone(event.to_at, timezone) : 'продолжается';
  const clipped = [
    event.clipped_left ? 'началось раньше окна' : '',
    event.clipped_right ? 'продолжается после окна' : '',
  ].filter(Boolean).join(', ');
  return [head, `${from}–${to}`, clipped].filter(Boolean).join('\n');
};

/** Индекс точки с frame_id или -1. Выбор только по точному id, не «ближайший на глаз». */
export const findPointIndex = (points, frameId) => (
  frameId == null ? -1 : points.findIndex((point) => String(point.frame_id) === String(frameId))
);

export const describeFrameGanttError = (error) => {
  const status = error?.response?.status;
  const detail = typeof error?.response?.data?.detail === 'string' ? error.response.data.detail.trim().toLowerCase() : '';
  if (status === 404 && detail === 'not found') return 'Диаграмма пока недоступна';
  if (status === 401) return 'Нужно войти заново';
  if (status === 403) return 'Недостаточно прав для просмотра этих данных';
  if (status === 404) return 'Камера не найдена';
  if (status === 422) return 'Слишком широкий период или слишком много событий. Сузьте окно.';
  if (status === 503) return 'Не удалось проверить права, доступ закрыт';
  if (error?.code === 'ECONNABORTED') return 'Сервис долго не отвечает. Повторите запрос.';
  return 'Не удалось загрузить диаграмму';
};
